// 居中 HUD 的状态单例 + 对外 API（与 Element Plus 的 ElMessage 同形，便于 codemod 迁移）。
// 无需 Pinia，直接用 Vue 的 reactive 做轻量单例。
import { reactive } from 'vue';

export type HudType = 'success' | 'error' | 'warning' | 'info' | 'loading' | 'undo';

export interface HudItem {
  id: number;
  type: HudType;
  text: string;
  duration: number;      // ms；0 = 不自动关闭（loading 用）
  undo?: () => void;     // 传了就在卡片里显示「撤销」按钮
  _timer?: number;
}

interface HudOpts {
  duration?: number;
  undo?: () => void;
}

const state = reactive<{ items: HudItem[] }>({ items: [] });
let seed = 0;

// 各类型默认自动关闭时长（ms）
const DEFAULT_DURATION: Record<HudType, number> = {
  success: 2000,
  info: 2000,
  warning: 2500,
  error: 3000,
  loading: 0,   // 手动关闭
  undo: 5000,   // 留足撤销时间
};

// ElMessage 兼容：允许 hud.warning({ message, duration }) 的对象入参；
// Error 对象取 message——两类都别渲染成 "[object Object]"/JSON 字符串。
type HudInput = string | Error | { message?: string; duration?: number };

function normalizeInput(input: HudInput, opts: HudOpts): { text: string; opts: HudOpts } {
  if (typeof input === 'string') return { text: input, opts };
  if (input instanceof Error) return { text: input.message || String(input), opts };
  if (input && typeof input === 'object') {
    return {
      text: typeof input.message === 'string' ? input.message : String(input.message ?? ''),
      opts: { duration: input.duration, ...opts },
    };
  }
  return { text: String(input ?? ''), opts };
}

function push(type: HudType, input: HudInput, rawOpts: HudOpts = {}): () => void {
  const { text, opts } = normalizeInput(input, rawOpts);
  const id = ++seed;
  const duration = opts.duration ?? DEFAULT_DURATION[type];
  const item: HudItem = { id, type, text, duration, undo: opts.undo };
  state.items.push(item);
  if (duration > 0) {
    item._timer = window.setTimeout(() => close(id), duration);
  }
  return () => close(id);
}

export function close(id: number): void {
  const i = state.items.findIndex((x) => x.id === id);
  if (i > -1) {
    if (state.items[i]._timer) clearTimeout(state.items[i]._timer);
    state.items.splice(i, 1);
  }
}

export function closeAll(): void {
  state.items.forEach((x) => x._timer && clearTimeout(x._timer));
  state.items.splice(0, state.items.length);
}

// ElMessage 同形 API：hud.success('保存成功') 等
export const hud = {
  success: (text: HudInput, opts?: HudOpts) => push('success', text, opts),
  error: (text: HudInput, opts?: HudOpts) => push('error', text, opts),
  warning: (text: HudInput, opts?: HudOpts) => push('warning', text, opts),
  info: (text: HudInput, opts?: HudOpts) => push('info', text, opts),
  /** 不自动关闭，返回 close 函数，请求结束后调用 */
  loading: (text: HudInput, opts?: HudOpts) => push('loading', text, { duration: 0, ...opts }),
  /** 带「撤销」按钮，cb 为点击撤销时的回调 */
  undo: (text: HudInput, cb: () => void, opts?: HudOpts) => push('undo', text, { undo: cb, ...opts }),
};

// 供 ToastHud.vue 读取
export const hudState = state;
